/**
 * Смарт-кнопки CRM: «МП.Товар N», «Модель N», «Мод.Склейка N» с меню как в CRM.
 *
 * Спека — `docs/specs/2026-09-17-products-select-filters-design.md`, раздел 2.
 * Меню РАСКРЫВАЕТСЯ ВВЕРХ по умолчанию: полоса выбора прилипает к низу рабочей
 * области. Пропом `down` — вниз (`top-full`): «Планы» так не прилипают.
 * Пропом `keys` — ограничить набор меню (задача 8, спека …-plans-export-smart).
 *
 * ПОДМЕНЮ — ШАГАМИ В ОДНОЙ ПАНЕЛИ, А НЕ ВЫЛЕТАМИ ПО НАВЕДЕНИЮ, как в CRM. Меню
 * живёт внутри `<main overflow-y-auto>` раздела, и вылеты там не помещаются:
 * всё выше верха `main` обрезается, всё правее уходит в его горизонтальную
 * прокрутку. Замер живой проверки 17.09.26 на 1280×800: у «МП.Товар» за край
 * выходили 22 подменю, у «Модели» 18, у «Вида» срезан верх, а пункты «SEO ›
 * Экспорт › Джем» не доставались мышью вовсе — вылетам нужно около 1460×860 px,
 * а ноутбук 1920×1080 при масштабе 125 % даёт ~1536×740. Портал в `body` снял
 * бы обрезку, но не ширину: три уровня вбок шире экрана ноутбука сами по себе.
 *
 * Поэтому панель одна, ширина постоянная, высота — не выше верха `main` от
 * кнопки, лишнее прокручивается внутри. Группа — строка-кнопка: клик уводит на
 * уровень вниз, первая строка уровня «← путь» — на уровень вверх. Группы,
 * подписи, порядок и адреса — ровно `CRM_MENUS`: спека требует меню CRM, а не
 * механику наведения. Заодно меню стало доступно с клавиатуры: строки — кнопки
 * и ссылки, Enter и пробел работают сами, Esc закрывает.
 */

import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { Suspense, use, useEffect, useLayoutEffect, useRef, useState } from "react";

import { type SmartIds, type SmartKey, type SmartNode, isGroup, loadCrmMenus, smartHref } from "@/lib/crmSmartButtons";
import { nf } from "@/lib/format";
import { plural } from "@/lib/plans";
import { MAX_LIST } from "@/lib/productNav";
import { usePopover } from "@/lib/usePopover";

import { КНОПКА, место, type Место, СТРОКА, ШИРИНА } from "./smartStyle";

const НОМЕРА = ["номеру", "номерам", "номерам"] as const;
/** Меню без номеров при `showEmpty` — не прячется, а гаснет с подсказкой чего нет. */
const ЧЕГО_НЕТ: Partial<Record<SmartKey, string>> = {
  supplyOrderID: "заказов",
  barcodeQrDataID: "QR-папок",
  mpStockSupplyPlanDateID: "партий плана",
  mpStockSupplyPlanID: "строк плана",
  mediaFileID: "карго-накладных",
  cryptoID: "оплат",
  supplySupplierID: "поставщиков",
  supplyOrderProductPositionID: "позиций заказа",
  serial: "серий",
  mpReturnID: "возвратов CRM",
};

type SmartButtonsProps = {
  ids: SmartIds;
  /** Ограничить набор меню — по умолчанию все меню, у которых есть номера. */
  keys?: SmartKey[];
  /** Раскрыть панель вниз (`top-full`) вместо вверх (`bottom-full`) — «Планы»
   *  не прилипают к низу рабочей области, как полоса выбора «Товаров». */
  down?: boolean;
  /** Галка у кнопки. Без неё — строка «Планов»: на 1280 px пять меню с галками
   *  не влезают в строку фиксированной высоты (замер 25.09.26). */
  caret?: boolean;
  /** Меню без номеров — неактивной кнопкой с подсказкой, а не пропуском
   *  (спека …-plans-export-smart, часть 1; «Заказы» — e4dc738): у «Планов» и
   *  «Заказов» набор меню постоянный, и пропавшее «Крипто» читалось бы как
   *  поломка. «Товары» — без: там меню без номеров просто нет. */
  showEmpty?: boolean;
};

/**
 * Меню CRM едут отдельным чанком (`loadCrmMenus`), и граница ожидания — СВОЯ,
 * а не граница экрана: кнопки встают и после выбора строки («Товары»,
 * «Заказы») обычным обновлением состояния, не переходом, и без своей границы
 * React на время загрузки погасил бы весь экран до заставки. Заглушка держит
 * высоту строки кнопок; чанк на вкладку едет один раз.
 */
export function SmartButtons(props: SmartButtonsProps) {
  return (
    <Suspense fallback={<div className="h-7" />}>
      <SmartButtonsLoaded {...props} />
    </Suspense>
  );
}

function SmartButtonsLoaded({ ids, keys, down = false, caret = true, showEmpty = false }: SmartButtonsProps) {
  const menus = use(loadCrmMenus());
  const [open, setOpen] = useState<SmartKey | null>(null);
  // Путь по группам — индексы в `nodes`: подписи групп CRM не обязаны быть
  // уникальными, номер места — обязан.
  const [путь, setПуть] = useState<number[]>([]);
  const [размещение, setРазмещение] = useState<Место>({ maxHeight: 400, right: false });
  const box = useRef<HTMLDivElement>(null);
  const кнопка = useRef<HTMLButtonElement | null>(null);
  const панель = useRef<HTMLDivElement>(null);

  const закрыть = () => {
    setOpen(null);
    setПуть([]);
  };

  usePopover(open !== null, закрыть, box);

  useEffect(() => {
    if (open === null) return;
    const мерить = () => {
      if (кнопка.current) setРазмещение(место(кнопка.current, down));
    };
    window.addEventListener("resize", мерить);
    return () => window.removeEventListener("resize", мерить);
  }, [open, down]);

  // Мерить ДО отрисовки: иначе панель на кадр встала бы со старой высотой.
  useLayoutEffect(() => {
    if (open !== null && кнопка.current) setРазмещение(место(кнопка.current, down));
  }, [open, down]);

  // Новый уровень — сначала: прокрутка прежнего уровня к нему отношения не
  // имеет, а фокус с нажатой строки исчез вместе с ней — клавиатура продолжает
  // с первой строки уровня. Только после шага, а не при открытии: открытое
  // кликом меню не должно забирать фокус у кнопки.
  const шагнули = useRef(false);
  const шагнуть = (next: number[]) => {
    шагнули.current = true;
    setПуть(next);
  };
  useEffect(() => {
    const p = панель.current;
    if (!p) return;
    p.scrollTop = 0;
    if (шагнули.current) p.querySelector<HTMLElement>("button, a")?.focus();
    шагнули.current = false;
  }, [путь]);

  const сегодня = new Date();
  return (
    <div ref={box} className="flex flex-wrap items-center gap-1.5" data-annot="смарт-кнопки">
      {menus.filter((menu) => !keys || keys.includes(menu.key)).map((menu) => {
        const номера = ids[menu.key] ?? [];
        if (номера.length === 0) {
          if (!showEmpty) return null;
          return (
            <button
              key={menu.key}
              type="button"
              disabled
              title={`у выбранных нет ${ЧЕГО_НЕТ[menu.key] ?? "номеров для этого меню"}`}
              className={`${КНОПКА} cursor-not-allowed opacity-50`}
            >
              {menu.label} <b className="tnum font-medium">0</b>
              {!caret ? null : down ? (
                <ChevronDown className="h-3.5 w-3.5 text-fg-faint" />
              ) : (
                <ChevronUp className="h-3.5 w-3.5 text-fg-faint" />
              )}
            </button>
          );
        }
        // Уровень пути: группы по индексам, подписи — для строки «назад».
        let nodes: SmartNode[] = menu.nodes;
        const подписи: string[] = [];
        for (const i of open === menu.key ? путь : []) {
          const node = nodes[i];
          if (!node || !isGroup(node)) break;
          подписи.push(node.label);
          nodes = node.items;
        }
        return (
          <div key={menu.key} className="relative">
            <button
              type="button"
              aria-expanded={open === menu.key}
              aria-haspopup="menu"
              onClick={(e) => {
                кнопка.current = e.currentTarget;
                setПуть([]);
                setOpen(open === menu.key ? null : menu.key);
              }}
              title={
                номера.length > MAX_LIST
                  ? `в адрес CRM уйдут первые ${nf.format(MAX_LIST)} из ${nf.format(номера.length)}: длиннее ~8 КБ CRM отвечает 414`
                  : `меню CRM по ${nf.format(номера.length)} ${plural(номера.length, НОМЕРА)}`
              }
              className={`${КНОПКА} hover:bg-bg-hover`}
            >
              {menu.label} <b className="tnum font-medium">{nf.format(номера.length)}</b>
              {!caret ? null : down ? (
                <ChevronDown className="h-3.5 w-3.5 text-fg-faint" />
              ) : (
                <ChevronUp className="h-3.5 w-3.5 text-fg-faint" />
              )}
            </button>
            {open === menu.key && (
              <div
                ref={панель}
                role="menu"
                aria-label={[menu.label, ...подписи].join(" › ")}
                style={{ width: ШИРИНА, maxHeight: размещение.maxHeight }}
                className={
                  "absolute z-50 overflow-y-auto overscroll-contain rounded-[var(--radius-ui)] border border-border bg-bg-elevated py-1 shadow-lg " +
                  (down ? "top-full mt-1 " : "bottom-full mb-1 ") +
                  (размещение.right ? "right-0" : "left-0")
                }
                data-annot={`меню ${menu.label}`}
              >
                {подписи.length > 0 && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => шагнуть(путь.slice(0, -1))}
                    title={`назад: ${[menu.label, ...подписи.slice(0, -1)].join(" › ")}`}
                    className={`${СТРОКА} mb-1 justify-start border-b border-border font-medium`}
                  >
                    <span className="truncate">← {подписи.join(" › ")}</span>
                  </button>
                )}
                <Уровень
                  nodes={nodes}
                  ids={номера}
                  today={сегодня}
                  onGroup={(i) => шагнуть([...путь, i])}
                  onDone={закрыть}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Уровень({
  nodes,
  ids,
  today,
  onGroup,
  onDone,
}: {
  nodes: SmartNode[];
  ids: number[];
  today: Date;
  onGroup: (index: number) => void;
  onDone: () => void;
}) {
  return (
    <>
      {nodes.map((node, i) => {
        if (isGroup(node)) {
          return (
            <button
              key={`группа:${i}:${node.label}`}
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              onClick={() => onGroup(i)}
              className={СТРОКА}
            >
              <span className="truncate">{node.label}</span>
              <ChevronRight className="h-3.5 w-3.5 flex-none text-fg-faint" />
            </button>
          );
        }
        const href = smartHref(node.href, ids, today);
        if (node.confirm) {
          const вопрос = node.confirm;
          // ДЕЙСТВУЕТ СРАЗУ при открытии (ставит задачи, «Объединить») — и
          // потому НЕ ССЫЛКА: у `<a href>` средняя кнопка и «открыть в новой
          // вкладке» открывают адрес мимо `onClick`, то есть мимо вопроса.
          return (
            <button
              key={node.id}
              type="button"
              role="menuitem"
              onClick={() => {
                if (!window.confirm(вопрос)) return;
                window.open(href, "_blank", "noopener");
                onDone();
              }}
              title={вопрос}
              className={СТРОКА}
            >
              <span className="truncate">{node.label}</span>
            </button>
          );
        }
        return (
          <a
            key={node.id}
            role="menuitem"
            href={href}
            target="_blank"
            rel="noopener"
            className={СТРОКА}
            onClick={onDone}
          >
            <span className="truncate">{node.label}</span>
          </a>
        );
      })}
    </>
  );
}
